import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { DeleteMemberModal } from "Bands/containers";
import { useBandContext, usePermissionsContext } from "Bands/contexts";
import { BAND_PATHS } from "Bands/router";
import { dispatchGetBandUsers } from "Bands/state";
import { IBandUser } from "Bands/types";
import { getMembersActions, useBandRoute, useGetMemberTableColumns } from "Bands/utils";
import { copy } from "shared/assets";
import { User } from "shared/assets/icons";
import { Card, IActionDropdownItem, Table } from "shared/components";
import { getIdFromPath, mapUserPermissions } from "shared/utils";
import { API_ENDPOINTS } from "shared/constants";

const {
  members: { membersTable: tableCopy },
} = copy;

export const MembersTable = () => {
  const { userCanManageMembers } = usePermissionsContext();

  const dispatch = useDispatch();
  const { bandId, bandUsers: members } = useBandContext();
  const { pushRoute } = useBandRoute();

  const [showDeleteModal, setShowDeleteModal] = useState<IBandUser | null>(null);

  useEffect(() => {
    if (bandId) {
      dispatchGetBandUsers(dispatch, bandId);
    }
  }, [bandId, dispatch]);

  const handleDeleteSuccess = () => {
    if (bandId) {
      dispatchGetBandUsers(dispatch, bandId);
    }
  };

  const getRowActions = useCallback(
    (memberId: string): IActionDropdownItem[] => {
      const member = members.find(({ id }) => memberId === id);

      if (!member) return [];

      const actions = getMembersActions({
        handleView: () => pushRoute(BAND_PATHS.VIEW_MEMBER, { params: { userId: memberId } }),
        handleEdit: () => pushRoute(BAND_PATHS.EDIT_MEMBER, { params: { userId: memberId } }),
        handleEditPermissions: () =>
          pushRoute(BAND_PATHS.EDIT_PERMISSION, {
            params: {
              userId: getIdFromPath(member.id, API_ENDPOINTS.BAND_USERS) || "",
            },
          }),
        handleDelete: () => {
          setShowDeleteModal(member);
        },
        canView: !!mapUserPermissions(member.permissions || []).VIEW_FULL_PROFILE,
        canManage: userCanManageMembers,
      });

      return actions;
    },
    [members, pushRoute, userCanManageMembers]
  );

  const { columns } = useGetMemberTableColumns(members[0]);

  if (!members.length) return null;

  return (
    <>
      {showDeleteModal && (
        <DeleteMemberModal
          member={showDeleteModal}
          handleClose={() => setShowDeleteModal(null)}
          handleDeleteSuccess={handleDeleteSuccess}
        />
      )}
      <Card title={tableCopy.title} icon={User}>
        <Table
          data={members}
          columns={columns}
          getRowActions={getRowActions}
          dataTestId="members_table"
          options={{ showActions: userCanManageMembers }}
        />
      </Card>
    </>
  );
};
